import React, { useState, useEffect } from 'react'
import { Tabs, Select } from 'antd'
import CommonTablePage, { CommonTablePagePropsInterface } from '../../components/common/common-table/common-table-page'
import { useDocumentTitle } from '../../services/title/useDocumentTitle'
import { DatePicker, Button } from 'antd'
import { DownloadOutlined } from '@ant-design/icons'
import moment from 'moment'
import { ApiRequest } from '../../services/api/api'
import './stock-log-page.css'

const { RangePicker } = DatePicker
const { Option } = Select
const { TabPane } = Tabs

interface DateRange {
  startDate: string
  endDate: string
}

const StockLogPage = () => {
  useDocumentTitle()
  const [activeTab, setActiveTab] = useState('1')
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: moment().subtract(1, 'month').format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD')
  })
  const [triggerResetData, setTriggerResetData] = useState<number>(Date.now())
  const [tableData, setTableData] = useState<Record<string, any[]>>({})
  const [productOptions, setProductOptions] = useState<{value: number, label: string}[]>([])
  const [selectedProductId, setSelectedProductId] = useState<number | undefined>(undefined)

  // Fetch product options on mount
  useEffect(() => {
    const fetchProductOptions = async () => {
      try {
        const res = await ApiRequest({
          url: 'Product/GetAllProductName',
          method: 'get'
        })
        const products = res.data.data || []
        const options = products.map((item: any) => ({
          label: `${item.productCode} - ${item.productName}`,
          value: item.productId
        }))
        setProductOptions(options)
      } catch (error) {
        console.error('Failed to fetch product options:', error)
      }
    }
    fetchProductOptions()
  }, [])

  const handleDateRangeChange = (dates: any) => {
    if (dates && dates[0] && dates[1]) {
      setDateRange({
        startDate: dates[0].format('YYYY-MM-DD'),
        endDate: dates[1].format('YYYY-MM-DD')
      })
      setTriggerResetData(Date.now())
    }
  }

  // 通用的数据保存函数
  const handleDataLoaded = (tabKey: string, data: any) => {
    const result = Array.isArray(data) ? data : (data?.data || [])
    setTableData(prev => ({ ...prev, [tabKey]: result }))
    return result
  }

  /**
   * 将本地日期范围转换为 UTC 时间戳
   * StockLog 的时间字段（如 time, createdAt）存储为 UTC 时间
   */
  const getDateRangeForApi = () => {
    const localStart = moment(dateRange.startDate).startOf('day')  // 本地 00:00:00
    const localEnd = moment(dateRange.endDate).endOf('day')        // 本地 23:59:59

    // 转换为 UTC ISO 格式时间戳
    const utcStartTimestamp = localStart.utc().format('YYYY-MM-DDTHH:mm:ss[Z]')
    const utcEndTimestamp = localEnd.utc().format('YYYY-MM-DDTHH:mm:ss[Z]')

    return { start: utcStartTimestamp, end: utcEndTimestamp }
  }

  // 导出 CSV
  const handleExportCsv = async (tabKey: string) => {
    try {
      const data = tableData[tabKey]
      if (!data || data.length === 0) {
        alert('No data to export. Please load data first.')
        return
      }

      let headers: string[] = []
      let csvRows: any[] = []

      if (tabKey === '1') {
        // 入库记录
        headers = ['From', 'Time', 'Operator', 'Product Code', 'Product Name', 'Qty']
        csvRows = [
          headers.join(','),
          ...data.map((row: any) => [
            `"${(row.from || '').replace(/"/g, '""')}"`,
            `"${row.time ? moment.utc(row.time).local().format('DD/MM/YYYY HH:mm') : ''}"`,
            `"${(row.operator || '').replace(/"/g, '""')}"`,
            `"${(row.productCode || '').replace(/"/g, '""')}"`,
            `"${(row.productName || '').replace(/"/g, '""')}"`,
            `"${row.qty || 0}"`
          ].join(','))
        ]
      } else if (tabKey === '2') {
        // 出库记录
        headers = ['Packing Slip No', 'Customer', 'Time', 'Operator', 'Product Code', 'Product Name', 'Qty']
        csvRows = [
          headers.join(','),
          ...data.map((row: any) => [
            `"${(row.packingSlipNo || '').replace(/"/g, '""')}"`,
            `"${(row.customer || '').replace(/"/g, '""')}"`,
            `"${row.time ? moment.utc(row.time).local().format('DD/MM/YYYY HH:mm') : ''}"`,
            `"${(row.operator || '').replace(/"/g, '""')}"`,
            `"${(row.productCode || '').replace(/"/g, '""')}"`,
            `"${(row.productName || '').replace(/"/g, '""')}"`,
            `"${row.qty || 0}"`
          ].join(','))
        ]
      } else if (tabKey === '3') {
        // 托盘移动记录
        headers = ['Time', 'Operator', 'Product Code', 'Qty', 'From Location', 'To Location']
        csvRows = [
          headers.join(','),
          ...data.map((row: any) => [
            `"${row.time ? moment.utc(row.time).local().format('DD/MM/YYYY HH:mm') : ''}"`,
            `"${(row.operator || '').replace(/"/g, '""')}"`,
            `"${(row.productCode || '').replace(/"/g, '""')}"`,
            `"${row.qty || 0}"`,
            `"${(row.fromLocation || '').replace(/"/g, '""')}"`,
            `"${(row.toLocation || '').replace(/"/g, '""')}"`
          ].join(','))
        ]
      } else if (tabKey === '4') {
        // 手工库存记录
        headers = ['Time', 'Operator', 'Product Code', 'Product Name', 'Qty', 'Type']
        csvRows = [
          headers.join(','),
          ...data.map((row: any) => [
            `"${row.time ? moment.utc(row.time).local().format('DD/MM/YYYY HH:mm') : ''}"`,
            `"${(row.operator || '').replace(/"/g, '""')}"`,
            `"${(row.productCode || '').replace(/"/g, '""')}"`,
            `"${(row.productName || '').replace(/"/g, '""')}"`,
            `"${row.qty || 0}"`,
            `"${(row.type || '').replace(/"/g, '""')}"`
          ].join(','))
        ]
      }

      const csvContent = csvRows.join('\n')
      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `StockLog_${activeTab}_${dateRange.startDate}_to_${dateRange.endDate}.csv`
      a.style.display = 'none'
      document.body.appendChild(a)
      a.click()
      setTimeout(() => {
        window.URL.revokeObjectURL(url)
        document.body.removeChild(a)
      }, 100)
    } catch (error: any) {
      console.error('Export failed:', error)
      alert('Export failed: ' + error.message)
    }
  }

  // 获取每个 Tab 的配置
  const getTabProps = (tabKey: string, displayKey: string, title: string, columns: any[]): CommonTablePagePropsInterface => {
    // 使用 UTC 时间戳进行查询
    const apiDateRange = getDateRangeForApi()
    let url = `StockLog/${tabKey}?start=${apiDateRange.start}&end=${apiDateRange.end}`
    url += `&productId=${selectedProductId}`

    // 调试日志
    console.log(`StockLog [${title}] - Date Range (UTC):`, {
      userSelected: `${dateRange.startDate} to ${dateRange.endDate}`,
      localInterpretation: {
        start: moment(dateRange.startDate).format('YYYY-MM-DD HH:mm:ss'),
        end: moment(dateRange.endDate).add(1, 'day').add(-1, 'second').format('YYYY-MM-DD HH:mm:ss'),
      },
      utcTimestamps: {
        start: moment(dateRange.startDate).utc().format('YYYY-MM-DDTHH:mm:ss[Z]'),
        end: moment(dateRange.endDate).add(1, 'day').add(-1, 'second').utc().format('YYYY-MM-DDTHH:mm:ss[Z]'),
      },
      sendingToApi: {
        start: apiDateRange.start,
        end: apiDateRange.end,
        url: url
      }
    })

    return {
      getAllUrl: url,
      title: title,
      column: columns,
      isNotEditable: true,
      isNotDeletable: true,
      isNotAddable: true,
      columnsButton: true,
      triggerResetData: triggerResetData,
      mappingRenderData: (data: any) => handleDataLoaded(displayKey, data)
    }
  }

  // Tab 1: 入库记录列定义
  const getStockInColumns = () => {
    const modelArr: any[] = []
    modelArr.push(
      {
        title: 'From',
        field: 'from',
        render: (rowData: any) => rowData.from || ''
      },
      {
        title: 'Time',
        field: 'time',
        type: 'datetime',
        render: (rowData: any) => rowData.time ? moment.utc(rowData.time).local().format('DD/MM/YYYY HH:mm') : ''
      },
      {
        title: 'Operator',
        field: 'operator',
        render: (rowData: any) => rowData.operator || ''
      },
      {
        title: 'Product Code',
        field: 'productCode',
        render: (rowData: any) => rowData.productCode || ''
      },
      {
        title: 'Product Name',
        field: 'productName',
        render: (rowData: any) => (
          <div style={{ maxWidth: '250px', whiteSpace: 'normal', wordBreak: 'break-word' }}>
            {rowData.productName || ''}
          </div>
        )
      },
      {
        title: 'Qty',
        field: 'qty',
        type: 'numeric',
        render: (rowData: any) => rowData.qty || 0
      }
    )
    return modelArr
  }

  // Tab 2: 出库记录列定义
  const getStockOutColumns = () => {
    const modelArr: any[] = []
    modelArr.push(
      {
        title: 'Packing Slip No',
        field: 'packingSlipNo',
        render: (rowData: any) => rowData.packingSlipNo || ''
      },
      {
        title: 'Customer',
        field: 'customer',
        render: (rowData: any) => rowData.customer || ''
      },
      {
        title: 'Time',
        field: 'time',
        type: 'datetime',
        render: (rowData: any) => rowData.time ? moment.utc(rowData.time).local().format('DD/MM/YYYY HH:mm') : ''
      },
      {
        title: 'Operator',
        field: 'operator',
        render: (rowData: any) => rowData.operator || ''
      },
      {
        title: 'Product Code',
        field: 'productCode',
        render: (rowData: any) => rowData.productCode || ''
      },
      {
        title: 'Product Name',
        field: 'productName',
        render: (rowData: any) => (
          <div style={{ maxWidth: '250px', whiteSpace: 'normal', wordBreak: 'break-word' }}>
            {rowData.productName || ''}
          </div>
        )
      },
      {
        title: 'Qty',
        field: 'qty',
        type: 'numeric',
        render: (rowData: any) => rowData.qty || 0
      }
    )
    return modelArr
  }

  // Tab 3: 托盘移动记录列定义
  const getRelocatedColumns = () => {
    const modelArr: any[] = []
    modelArr.push(
      {
        title: 'Time',
        field: 'time',
        type: 'datetime',
        render: (rowData: any) => rowData.time ? moment.utc(rowData.time).local().format('DD/MM/YYYY HH:mm') : ''
      },
      {
        title: 'Operator',
        field: 'operator',
        render: (rowData: any) => rowData.operator || ''
      },
      {
        title: 'Product Code',
        field: 'productCode',
        render: (rowData: any) => rowData.productCode || ''
      },
      {
        title: 'Qty',
        field: 'qty',
        type: 'numeric',
        render: (rowData: any) => rowData.qty || 0
      },
      {
        title: 'From Location',
        field: 'fromLocation',
        render: (rowData: any) => rowData.fromLocation || ''
      },
      {
        title: 'To Location',
        field: 'toLocation',
        render: (rowData: any) => rowData.toLocation || ''
      }
    )
    return modelArr
  }

  // Tab 4: 手工库存记录列定义
  const getManualStockColumns = () => {
    const modelArr: any[] = []
    modelArr.push(
      {
        title: 'Time',
        field: 'time',
        type: 'datetime',
        render: (rowData: any) => rowData.time ? moment.utc(rowData.time).local().format('DD/MM/YYYY HH:mm') : ''
      },
      {
        title: 'Operator',
        field: 'operator',
        render: (rowData: any) => rowData.operator || ''
      },
      {
        title: 'Product Code',
        field: 'productCode',
        render: (rowData: any) => rowData.productCode || ''
      },
      {
        title: 'Product Name',
        field: 'productName',
        render: (rowData: any) => (
          <div style={{ maxWidth: '250px', whiteSpace: 'normal', wordBreak: 'break-word' }}>
            {rowData.productName || ''}
          </div>
        )
      },
      {
        title: 'Qty',
        field: 'qty',
        type: 'numeric',
        render: (rowData: any) => rowData.qty || 0
      },
      {
        title: 'Type',
        field: 'type',
        render: (rowData: any) => rowData.type || ''
      }
    )
    return modelArr
  }

  const renderLogTable = (props: CommonTablePagePropsInterface) => {
    if (!selectedProductId) {
      return (
        <div style={{ padding: '48px 16px', textAlign: 'center', color: '#999' }}>
          Please select a product to view logs
        </div>
      )
    }
    return <CommonTablePage {...props} />
  }

  return (
    <div className="stock-log-page">
      <div className="date-range-picker">
        <span>Date Range: </span>
        <RangePicker
          value={[moment(dateRange.startDate), moment(dateRange.endDate)]}
          onChange={handleDateRangeChange}
          format="YYYY-MM-DD"
          allowClear={false}
        />
        <span style={{ marginLeft: '16px' }}>Product: </span>
        <Select
          style={{ width: 300 }}
          placeholder="Select a product"
          value={selectedProductId}
          onChange={(value) => {
            setSelectedProductId(value)
            setTriggerResetData(Date.now())
          }}
          showSearch
          filterOption={(input: string, option: any) =>
            (option?.children ?? '').toString().toLowerCase().includes(input.toLowerCase())
          }
        >
          {productOptions.map(option => (
            <Option key={option.value} value={option.value}>
              {option.label}
            </Option>
          ))}
        </Select>
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          onClick={() => handleExportCsv(activeTab)}
          style={{ marginLeft: '16px' }}
        >
          <span style={{ color: 'white' }}>Export to CSV</span>
        </Button>
      </div>
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab="Stock In Log" key="1">
          {renderLogTable(getTabProps('GetStockInLog', '1', 'Stock In Log', getStockInColumns()))}
        </TabPane>
        <TabPane tab="Stock Out Log" key="2">
          {renderLogTable(getTabProps('GetStockOutLog', '2', 'Stock Out Log', getStockOutColumns()))}
        </TabPane>
        <TabPane tab="Relocated Logs" key="3">
          {renderLogTable(getTabProps('GetRelocatedLogs', '3', 'Relocated Logs', getRelocatedColumns()))}
        </TabPane>
        <TabPane tab="Manual Stock Logs" key="4">
          {renderLogTable(getTabProps('GetManualStockLogs', '4', 'Manual Stock Logs', getManualStockColumns()))}
        </TabPane>
      </Tabs>
    </div>
  )
}

export default StockLogPage
